import React, { useRef, useState } from 'react';
import { Button, Flex, Icon, Table, Text } from '@pega/cosmos-react-core';
import SearchComponent from './SearchComponent';
import { useDispatch, useSelector } from 'react-redux';
import { appState } from '../../interfaces/common';
import { fetchErrorLogs } from '../../redux/actions';
import useLocalTranslation from '../../hooks/useLocalTranslation';
import AdditionalInfo from '../cosmos-core/AdditionalInfo';
import styled, { css } from 'styled-components';

const columns: any = [
  {
    id: 'timestamp',
    renderer: 'timestamp',
    label: 'Time (UTC)',
  },
  {
    id: 'reason',
    renderer: 'reason',
    label: 'Reason',
  },
  {
    id: 'http_status',
    renderer: 'http_status',
    label: 'Status',
  },
  {
    id: 'attempt',
    renderer: 'attempt',
    label: 'Attempt(s)',
  },
  {
    id: 'queue_time',
    renderer: 'queue_time',
    label: 'Queue time(sec)',
  },
  {
    id: 'duration',
    renderer: 'duration',
    label: 'Duration(sec)',
  },
  {
    id: 'connection_name',
    renderer: 'connection_name',
    label: 'Connection name',
  },
  {
    id: 'connection_id',
    renderer: 'connection_id',
    label: 'Connection ID',
  },
  {
    id: 'webhook',
    renderer: 'webhook',
    label: 'Webhook',
  },
  {
    id: 'requestId',
    renderer: 'requestId',
    label: 'Request ID',
  },
  {
    id: 'device_info',
    renderer: 'device_info',
    label: 'Device info',
  },
];

const reason = 'reason';

const StyledHeader = styled.h3(
  () => css`
    padding-left: 16px;
  `,
);

const ErrorLogs = () => {
  const { errorLogsData, nextToken, accounts, consumerId, managerId } = useSelector((state: appState) => state);
  const [payloadData, setPayloadData] = useState({});
  const dispatch = useDispatch();
  const tableRef = useRef<any>(null);
  const translate = useLocalTranslation();

  const connectionDetailsMap = React.useMemo(() => {
    const mappedAccountNames = accounts.reduce((mappedAccountNames: any, account: any) => {
      mappedAccountNames[account.connectionId] = account.title;
      return mappedAccountNames;
    }, {});
    return mappedAccountNames;
  }, [accounts]);

  const errorsData = errorLogsData?.reduce((errorData: any, errorLogData: any) => {
    if (errorLogData[reason]?.includes('ECONNREFUSED')) {
      errorLogData = { ...errorLogData, reason: translate('econnrefused') };
    } else if (errorLogData[reason]?.includes('EHOSTUNREACH')) {
      errorLogData = { ...errorLogData, reason: translate('ehostunreach') };
    } else if (errorLogData[reason]?.includes('ETIMEDOUT')) {
      errorLogData = { ...errorLogData, reason: translate('etimedout') };
    } else if (errorLogData[reason]?.includes('ECONNRESET')) {
      errorLogData = { ...errorLogData, reason: translate('econnreset') };
    } else if (errorLogData[reason]?.includes('socket hang up')) {
      errorLogData = { ...errorLogData, reason: translate('socket_hang_up') };
    } else if (errorLogData[reason]?.includes('timed out')) {
      errorLogData = { ...errorLogData, reason: translate('timed_out') };
    } else if (
      errorLogData[reason]?.includes('status code 500') ||
      errorLogData[reason]?.includes('status code 502') ||
      errorLogData[reason]?.includes('status code 503') ||
      errorLogData[reason]?.includes('status code 504')
    ) {
      errorLogData = { ...errorLogData, reason: translate('error_code_5xx') };
    } else if (
      errorLogData[reason]?.includes('status code 403') ||
      errorLogData[reason]?.includes('status code 401') ||
      errorLogData[reason]?.includes('status code 404') ||
      errorLogData[reason]?.includes('ENOTFOUND')
    ) {
      errorLogData = { ...errorLogData, reason: translate('status_code_4xx') };
    } else {
      errorLogData = { ...errorLogData, reason: translate('unknown_error') };
    }
    errorLogData['connection_name'] = connectionDetailsMap[errorLogData.connection_id];
    errorData.push(errorLogData);
    return errorData;
  }, []);

  if (!errorsData?.[0]?.device_info && columns[columns.length - 1].id === 'device_info') {
    columns.pop();
  }

  const handleClick = () => {
    dispatch(fetchErrorLogs({ ...payloadData, nextToken }));
    tableRef.current.scrollIntoView();
  };

  return (
    <>
      <SearchComponent setPayloadData={setPayloadData} />
      <Flex container={{ itemGap: 1 }}>
        <StyledHeader>{translate('error_logs')}</StyledHeader>
        <AdditionalInfo heading={translate('col_definition_info')}>
          <Flex>
            {columns.map((column: any) => {
              return (
                <p key={column.id}>
                  <strong>{column.label}: </strong>
                  {translate(column.renderer + '_info')}
                </p>
              );
            })}
          </Flex>
        </AdditionalInfo>
      </Flex>
      <Flex container={{}} style={{ paddingInlineStart: '16px', marginBlock: '8px' }}>
        <Text variant="h3">For Consumer ID: </Text>
        <Text variant="h3"> {consumerId}</Text>
        <Text variant="h3" style={{ paddingInlineStart: '16px' }}>
          Manager ID:{' '}
        </Text>
        <Text variant="h2"> {managerId}</Text>
      </Flex>
      <Table data={errorsData} columns={columns} ref={tableRef} data-testid="error-log-table" />
      {nextToken && (
        <Flex container={{ justify: 'center' }} data-testid="load-more-errors">
          <Button variant="link" onClick={handleClick}>
            Load More data
            <Icon name="arrow-micro-down" />
          </Button>
        </Flex>
      )}
    </>
  );
};

export default ErrorLogs;
